import React from 'react'

import { useTranslation } from 'react-i18next'

import {
  ArcadePasswordInput,
  ArcadePasswordInputProps,
  Input,
  PasswordInput as RawPasswordInput,
} from '@cash-design-system/react'

import { FieldController } from '../FieldController/FieldController'

type PasswordInputProps = Omit<
  ArcadePasswordInputProps,
  'fieldLabelProps' | 'hidePasswordLabel' | 'showPasswordLabel' | 'isNewPassword'
> &
  Partial<Pick<ArcadePasswordInputProps, 'fieldLabelProps' | 'isNewPassword'>>

export const PasswordInput = React.forwardRef<HTMLInputElement, PasswordInputProps>(
  ({ name, placeholder, isNewPassword = false, fieldLabelProps = { label: '' }, ...rest }, ref) => {
    const { t } = useTranslation()

    return (
      <FieldController
        FieldComponent={ArcadePasswordInput}
        componentProps={{
          ...rest,
          fieldLabelProps: fieldLabelProps,
          isNewPassword: isNewPassword,
          placeholder: t('Enter password', { context: 'placeholder' }),
          hidePasswordLabel: t('Hide password', { context: 'aria label for button to hide password in form field' }),
          showPasswordLabel: t('Show password', { context: 'aria label for button to show password in form field' }),
        }}
        name={name || (isNewPassword ? 'new-password' : 'current-password')}
        rules={{
          required: {
            value: true,
            message: t('Password is required.', { context: 'error message for password form field' }),
          },
        }}
      />
    )
  }
)

export const PasswordInputLegacy = React.forwardRef<HTMLInputElement, React.ComponentProps<typeof Input>>(
  (props, ref) => {
    const { t } = useTranslation()
    return (
      <RawPasswordInput
        {...props}
        ref={ref}
        hidePasswordLabel={t('Hide password', { context: 'aria label for button to hide password in form field' })}
        showPasswordLabel={t('Show password', { context: 'aria label for button to show password in form field' })}
      />
    )
  }
)
